import React from "react";

/**
 * Unified status pill. Maps common HR/payroll/attendance statuses to consistent tone colors.
 * Additional custom statuses fall through to a neutral gray pill.
 *
 * Usage:
 *   <StatusBadge status="present" />
 *   <StatusBadge status="approved" label="Approved by HR" />
 *   <StatusBadge status="pending" testid="badge-x" />
 */

const TONE = {
    success: { bg: "rgba(14, 159, 110, 0.10)", fg: "#0E7A55", ring: "rgba(14, 159, 110, 0.28)" },
    warning: { bg: "rgba(245, 158, 11, 0.12)", fg: "#B4630D", ring: "rgba(245, 158, 11, 0.30)" },
    danger:  { bg: "rgba(220, 38, 38, 0.10)",  fg: "#B41818", ring: "rgba(220, 38, 38, 0.28)" },
    info:    { bg: "rgba(37, 99, 235, 0.10)",  fg: "#1D4ED8", ring: "rgba(37, 99, 235, 0.28)" },
    neutral: { bg: "rgba(100, 116, 139, 0.12)", fg: "#475569", ring: "rgba(100, 116, 139, 0.28)" },
};

const STATUS_MAP = {
    // attendance
    present: "success",
    on_time: "success",
    late: "warning",
    absent: "danger",
    exception: "danger",
    pending_review: "warning",
    approved: "success",
    rejected: "danger",
    // payroll / periods
    draft: "neutral",
    prepared: "info",
    dept_confirmed: "info",
    finance_reviewed: "info",
    funded: "info",
    paid: "success",
    closed: "success",
    cancelled: "neutral",
    // funding
    requested: "warning",
    funds_received: "info",
    released: "info",
    confirmed: "success",
    partial: "warning",
    // contracts / employment
    active: "success",
    inactive: "neutral",
    expired: "danger",
    expiring: "warning",
    // discipline / leave / tasks
    issued: "info",
    acknowledged: "info",
    resolved: "success",
    pending: "warning",
    in_progress: "info",
    completed: "success",
    overdue: "danger",
    // ppe
    returned: "neutral",
    replaced: "neutral",
    lost: "danger",
    damaged: "danger",
};

function toneFor(status) {
    if (!status) return "neutral";
    return STATUS_MAP[String(status).toLowerCase()] || "neutral";
}

function labelFor(status, override) {
    if (override) return override;
    const s = String(status || "").replace(/_/g, " ");
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
}

export default function StatusBadge({ status, label, tone: toneOverride, testid, className = "" }) {
    const t = TONE[toneOverride || toneFor(status)];
    const text = labelFor(status, label);
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${className}`}
            style={{
                backgroundColor: t.bg,
                color: t.fg,
                boxShadow: `inset 0 0 0 1px ${t.ring}`,
                lineHeight: 1.4,
            }}
            data-testid={testid || `status-badge-${String(status || "unknown").toLowerCase()}`}
        >
            {text}
        </span>
    );
}
